import { Table, TableBody } from '@one-thd/sui-atomic-components';
import { ErrorBoundary } from '@thd-olt-component-react/error-boundary';
import { AxiosError } from 'axios';
import React, { Fragment, useEffect, useState } from 'react';
import { usePurchaseHistoryContext } from '../../../hooks/useContexts';
import { IRecord } from '../../../models/purchase-history';
import {
  ITableView,
  TSelectedRow,
} from '../../../models/purchase-history/table';
import { TableSkeleton } from '../../shared/skeletons/TableSkeleton';
import NoOrdersView from '../NoOrdersView';
import { PHPagination } from '../PHPagination';
import { PHTableHeader, PHTableRow, PHTableSettingsDrawer } from './index';
import { PHTableHeaderValues } from './PHTableHeaderData';

export const PHTable = ({
  customerRole,
  customerType,
  orderHistoryData,
  orderHistoryLoaded,
  pHRequest,
  setPHRequest,
  enableSettingsDrawer,
  setEnableSettingsDrawer,
}: ITableView) => {
  const {
    orderHistoryError,
    setAlertState,
    setOpenAddReceiptDrawer,
    dateSelectorValue,
  } = usePurchaseHistoryContext();
  const [selected, setSelected] = useState<TSelectedRow>({ 0: 0 });
  const [key, setKey] = useState(0);

  const [tableHeaderState, setTableHeaderState] = useState(
    PHTableHeaderValues(),
  );

  useEffect(() => {
    setSelected({ 0: 0 });
  }, [pHRequest.pageNumber, pHRequest.sortBy, pHRequest.sortOrder]);

  const phData = orderHistoryData?.orders || orderHistoryData?.lineItems;

  const isAdminRole = customerRole === 'ADMIN';

  const searchEnabled = pHRequest.searchBy !== undefined;

  const searchByObj = pHRequest?.searchBy?.[0] ?? {};
  const isLineItemData = ['itemDescription'].includes(
    Object.keys(searchByObj)?.[0],
  );
  const isPaymentColumnEnabled = tableHeaderState?.find(
    (header: {
      columnName: string;
      enabled: boolean;
      showToggle: string | number | boolean;
    }) => header?.columnName === 'Payment',
  )?.enabled;
  // This is to fetch the order details and display the payment methods in the table after performing a search, if the payment column is enabled and the search is not done by item description.
  const fetchOrderDetailsAfterSearch =
    !isLineItemData && isPaymentColumnEnabled && searchEnabled;

  const totalNumberOfOrders =
    orderHistoryData?.orderCount || orderHistoryData?.itemsCount || 0;

  // This is to force the table to rerender since style changes weren't being applied on first render
  useEffect(() => {
    setKey((prevKey) => prevKey + 1);
  }, []);

  useEffect(() => {
    if ((orderHistoryError as AxiosError)?.response?.status === 500) {
      setAlertState({
        alertTitle: 'Purchase History Unavailable',
        alertMessage:
          "We're sorry, there is a technical error. Please try again later.",
        status: 'error',
      });
      return;
    }

    setAlertState(null);
  }, [orderHistoryError]);

  if (
    orderHistoryLoaded &&
    (totalNumberOfOrders === 0 ||
      orderHistoryData?.orders?.length === 0 ||
      (orderHistoryError as AxiosError)?.response?.status !== undefined)
  ) {
    return (
      <ErrorBoundary name="no-orders">
        <NoOrdersView
          customerType={customerType}
          setOpenAddReceiptModal={setOpenAddReceiptDrawer}
          activeFilter={dateSelectorValue === 'last6Months'}
        />
      </ErrorBoundary>
    );
  }

  return (
    <div className="sui-overflow-x-scroll sui-mb-5" key={key}>
      <Table
        unlined
        layout="fixed"
        density="loose"
        className="sui-shadow-none"
        style={{ borderBottom: '1px solid #000', width: '100%' }}
      >
        <PHTableHeader
          isAdminRole={isAdminRole}
          pHRequest={pHRequest}
          setPHRequest={setPHRequest}
          orderHistoryLoaded={orderHistoryLoaded}
          tableHeaderState={tableHeaderState}
        />
        <TableBody>
          {orderHistoryLoaded ? (
            phData?.map((order: IRecord, index: number) => (
              <Fragment key={index}>
                <PHTableRow
                  customerType={customerType}
                  index={index}
                  isAdminRole={isAdminRole}
                  order={order}
                  orderHistoryData={phData}
                  pHRequest={pHRequest}
                  searchEnabled={searchEnabled}
                  selected={selected}
                  setAlertState={setAlertState}
                  setSelected={setSelected}
                  tableHeaderState={tableHeaderState}
                  fetchOrderDetailsAfterSearch={fetchOrderDetailsAfterSearch}
                />
              </Fragment>
            ))
          ) : (
            <TableSkeleton colSpan={tableHeaderState?.length ?? 9} />
          )}
        </TableBody>
      </Table>
      <ErrorBoundary name="purchase-history-table-pagination">
        <PHPagination />
      </ErrorBoundary>
      {enableSettingsDrawer && (
        <PHTableSettingsDrawer
          enableSettingsDrawer={enableSettingsDrawer}
          setEnableSettingsDrawer={setEnableSettingsDrawer}
          tableHeaderState={tableHeaderState}
          setTableHeaderState={setTableHeaderState}
        />
      )}
    </div>
  );
};
